import React, { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type ProductBookingSlot } from '../types/productBookingSlot';

const HIGHLIGHT_DURATION_MS = 3200;
const TOOLTIP_DURATION_MS = 3000;

type SequentialVisualState = {
  highlightedSlot: ProductBookingSlot | null;
  tooltipAnchor: ProductBookingSlot | null;
  tooltipOpen: boolean;
  tooltipTitle: string;
};

const initialVisual: SequentialVisualState = {
  highlightedSlot: null,
  tooltipAnchor: null,
  tooltipOpen: false,
  tooltipTitle: '',
};

type SequentialContextValue = {
  visual: SequentialVisualState;
  notifySequentialFeedback: (
    tooltipAnchor: ProductBookingSlot,
    highlightSlot: ProductBookingSlot,
    tooltipTitle: string,
  ) => void;
  clearSequentialUi: () => void;
};

export const SequentialContext = createContext<SequentialContextValue | null>(null);

export const SequentialStepsProvider = ({ children }: PropsWithChildren) => {
  const [visual, setVisual] = useState<SequentialVisualState>(initialVisual);
  const timersRef = useRef<{ highlight?: ReturnType<typeof setTimeout>; tooltip?: ReturnType<typeof setTimeout> }>({});

  const clearTimers = useCallback(() => {
    if (timersRef.current.highlight) clearTimeout(timersRef.current.highlight);
    if (timersRef.current.tooltip) clearTimeout(timersRef.current.tooltip);

    timersRef.current = {};
  }, []);

  const clearSequentialUi = useCallback(() => {
    clearTimers();
    setVisual(initialVisual);
  }, [clearTimers]);

  const notifySequentialFeedback = useCallback(
    (tooltipAnchor: ProductBookingSlot, highlightSlot: ProductBookingSlot, tooltipTitle: string) => {
      clearTimers();

      setVisual({
        highlightedSlot: highlightSlot,
        tooltipAnchor,
        tooltipOpen: true,
        tooltipTitle,
      });

      timersRef.current.highlight = setTimeout(() => {
        setVisual((prev) => ({ ...prev, highlightedSlot: null }));
      }, HIGHLIGHT_DURATION_MS);

      timersRef.current.tooltip = setTimeout(() => {
        setVisual((prev) => ({
          ...prev,
          tooltipAnchor: null,
          tooltipOpen: false,
          tooltipTitle: '',
        }));
      }, TOOLTIP_DURATION_MS);
    },
    [clearTimers],
  );

  useEffect(() => clearTimers, [clearTimers]);

  const value = useMemo(
    () => ({
      visual,
      notifySequentialFeedback,
      clearSequentialUi,
    }),
    [visual, notifySequentialFeedback, clearSequentialUi],
  );

  return <SequentialContext.Provider value={value}>{children}</SequentialContext.Provider>;
};
